import logger from '@/utils/logger'
import axios from "axios";
import { HTTP } from './repository';

// Enhanced eCommerce API service with secure guest access system
// Uses API key authentication instead of hardcoded credentials
export const ECommerceAPI = axios.create({
    baseURL: window.VUE_CONFIG_API_ECOMMERCE || 'https://paymentapi-ecommerce-test-v2.azurewebsites.net/api/ecommerce/'
});

// Session management for enhanced user experience
class SessionManager {
    constructor() {
        this.sessionKey = 'ecommerce-session';
        this.sessionCreatedKey = 'ecommerce-session-created';
        this.sessionTimeout = 60 * 60 * 1000; // 60 minutes
    }

    getOrCreateSession() {
        let sessionId = localStorage.getItem(this.sessionKey);
        const created = localStorage.getItem(this.sessionCreatedKey);

        // Check if session is expired
        if (sessionId && created) {
            const sessionAge = Date.now() - parseInt(created);
            if (sessionAge > this.sessionTimeout) {
                sessionId = null; // Expire the session
            }
        }

        if (!sessionId) {
            sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
            localStorage.setItem(this.sessionKey, sessionId);
            localStorage.setItem(this.sessionCreatedKey, Date.now().toString());
        }

        return sessionId;
    }

    clearSession() {
        localStorage.removeItem(this.sessionKey);
        localStorage.removeItem(this.sessionCreatedKey);
    }
}

const sessionManager = new SessionManager();

/**
 * Cart Persistence Configuration
 * Configurable settings for cart persistence behavior
 */
const CartPersistenceConfig = {
    // Storage keys
    cartKey: 'ecommerce-cart',
    cartTimestampKey: 'ecommerce-cart-timestamp',
    paymentInProgressKey: 'ecommerce-payment-in-progress',

    // Expiration times (in milliseconds)
    cartExpirationTime: 24 * 60 * 60 * 1000, // 24 hours
    paymentTimeoutTime: 30 * 60 * 1000, // 30 minutes

    // Behavior settings
    autoSaveOnCartChange: true,
    showRestoreNotification: true,
    enableBackendRecovery: true,
    enablePaymentReturnRecovery: true,

    // Debug settings
    enableDebugLogging: true,

    // Cleanup settings
    cleanupExpiredOnInit: true,
    maxStorageSize: 1024 * 1024 // 1MB limit for cart data
};

// Apply window configuration overrides if available
if (typeof window !== 'undefined' && window.CART_PERSISTENCE_CONFIG) {
    Object.assign(CartPersistenceConfig, window.CART_PERSISTENCE_CONFIG);
}

/**
 * Cart Persistence Manager
 * Handles cart storage, restoration, and cleanup across browser sessions
 */
class CartPersistenceManager {
    constructor(config = CartPersistenceConfig) {
        this.config = { ...CartPersistenceConfig, ...config };
        this.cartKey = this.config.cartKey;
        this.cartTimestampKey = this.config.cartTimestampKey;
        this.cartExpirationTime = this.config.cartExpirationTime;
        this.paymentInProgressKey = this.config.paymentInProgressKey;
        this.paymentTimeoutTime = this.config.paymentTimeoutTime;

        // Initialize cleanup if enabled
        if (this.config.cleanupExpiredOnInit) {
            this.cleanupExpiredData();
        }
    }

    /**
     * Save cart state to localStorage
     * @param {Object} cartState - The cart state from Vuex store
     */
    saveCart(cartState) {
        try {
            const cartData = {
                items: cartState.items || [],
                total: cartState.total || 0,
                guestInfo: cartState.guestInfo || {},
                taxAmount: cartState.taxAmount || 0,
                totalWithTax: cartState.totalWithTax || 0,
                timestamp: Date.now(),
                version: '1.0' // For future compatibility
            };

            const cartJson = JSON.stringify(cartData);

            // Check storage size limit
            if (this.config.maxStorageSize && cartJson.length > this.config.maxStorageSize) {
                logger.warn('🛒 Cart data exceeds size limit, truncating items');
                cartData.items = cartData.items.slice(0, Math.floor(cartData.items.length / 2));
                cartJson = JSON.stringify(cartData);
            }

            localStorage.setItem(this.cartKey, cartJson);
            localStorage.setItem(this.cartTimestampKey, Date.now().toString());

            if (this.config.enableDebugLogging) {
                logger.debug('🛒 Cart saved to localStorage:', {
                    itemCount: cartData.items.length,
                    total: cartData.total,
                    size: cartJson.length + ' bytes'
                });
            }
        } catch (error) {
            logger.warn('💥 Failed to save cart to localStorage:', error);

            // Attempt cleanup and retry once
            this.cleanupExpiredData();
            try {
                const simpleCartData = {
                    items: cartState.items || [],
                    total: cartState.total || 0,
                    timestamp: Date.now()
                };
                localStorage.setItem(this.cartKey, JSON.stringify(simpleCartData));
                localStorage.setItem(this.cartTimestampKey, Date.now().toString());
                logger.debug('🛒 Cart saved with simplified data after cleanup');
            } catch (retryError) {
                logger.warn('💥 Failed to save cart even after cleanup:', retryError);
            }
        }
    }

    /**
     * Restore cart state from localStorage
     * @returns {Object|null} - Restored cart state or null if not found/expired
     */
    restoreCart() {
        try {
            const cartData = localStorage.getItem(this.cartKey);
            const timestamp = localStorage.getItem(this.cartTimestampKey);

            if (!cartData || !timestamp) {
                logger.debug('🛒 No cart data found in localStorage');
                return null;
            }

            const cartAge = Date.now() - parseInt(timestamp);
            if (cartAge > this.cartExpirationTime) {
                logger.debug('🛒 Cart data expired, clearing localStorage');
                this.clearCart();
                return null;
            }

            const parsedCart = JSON.parse(cartData);

            // Validate cart structure
            if (!parsedCart.items || !Array.isArray(parsedCart.items)) {
                logger.warn('🛒 Invalid cart structure, clearing localStorage');
                this.clearCart();
                return null;
            }

            logger.debug('🛒 Cart restored from localStorage:', {
                itemCount: parsedCart.items.length,
                total: parsedCart.total,
                age: Math.round(cartAge / 1000 / 60) + ' minutes'
            });

            return {
                items: parsedCart.items,
                total: parsedCart.total || 0,
                guestInfo: parsedCart.guestInfo || {},
                taxAmount: parsedCart.taxAmount || 0,
                totalWithTax: parsedCart.totalWithTax || 0
            };
        } catch (error) {
            logger.warn('💥 Failed to restore cart from localStorage:', error);
            this.clearCart();
            return null;
        }
    }

    /**
     * Clear cart data from localStorage
     */
    clearCart() {
        try {
            localStorage.removeItem(this.cartKey);
            localStorage.removeItem(this.cartTimestampKey);
            localStorage.removeItem(this.paymentInProgressKey);
            logger.debug('🛒 Cart cleared from localStorage');
        } catch (error) {
            logger.warn('💥 Failed to clear cart from localStorage:', error);
        }
    }

    /**
     * Mark payment as in progress to prevent cart clearing during payment flow
     */
    markPaymentInProgress() {
        try {
            localStorage.setItem(this.paymentInProgressKey, Date.now().toString());
            logger.debug('💳 Payment marked as in progress');
        } catch (error) {
            logger.warn('💥 Failed to mark payment in progress:', error);
        }
    }

    /**
     * Clear payment in progress flag
     */
    clearPaymentInProgress() {
        try {
            localStorage.removeItem(this.paymentInProgressKey);
            logger.debug('💳 Payment in progress flag cleared');
        } catch (error) {
            logger.warn('💥 Failed to clear payment in progress flag:', error);
        }
    }

    /**
     * Check if payment is currently in progress
     * @returns {boolean}
     */
    isPaymentInProgress() {
        try {
            const paymentTimestamp = localStorage.getItem(this.paymentInProgressKey);
            if (!paymentTimestamp) return false;

            // Use configured payment timeout
            const paymentAge = Date.now() - parseInt(paymentTimestamp);

            if (paymentAge > this.paymentTimeoutTime) {
                this.clearPaymentInProgress();
                return false;
            }

            return true;
        } catch (error) {
            logger.warn('💥 Failed to check payment in progress:', error);
            return false;
        }
    }

    /**
     * Clean up expired cart and payment data
     */
    cleanupExpiredData() {
        try {
            const cartTimestamp = localStorage.getItem(this.cartTimestampKey);
            const paymentTimestamp = localStorage.getItem(this.paymentInProgressKey);

            let cleanedItems = [];

            // Clean up expired cart
            if (cartTimestamp) {
                const cartAge = Date.now() - parseInt(cartTimestamp);
                if (cartAge > this.cartExpirationTime) {
                    localStorage.removeItem(this.cartKey);
                    localStorage.removeItem(this.cartTimestampKey);
                    cleanedItems.push('expired cart');
                }
            }

            // Clean up expired payment flag
            if (paymentTimestamp) {
                const paymentAge = Date.now() - parseInt(paymentTimestamp);
                if (paymentAge > this.paymentTimeoutTime) {
                    localStorage.removeItem(this.paymentInProgressKey);
                    cleanedItems.push('expired payment flag');
                }
            }

            if (cleanedItems.length > 0 && this.config.enableDebugLogging) {
                logger.debug('🧹 Cleaned up:', cleanedItems.join(', '));
            }
        } catch (error) {
            logger.warn('💥 Failed to cleanup expired data:', error);
        }
    }

    /**
     * Get storage usage statistics
     * @returns {Object}
     */
    getStorageStats() {
        try {
            const cartData = localStorage.getItem(this.cartKey);
            const cartSize = cartData ? cartData.length : 0;

            return {
                cartSize,
                cartSizeKB: Math.round(cartSize / 1024 * 100) / 100,
                maxSizeKB: Math.round(this.config.maxStorageSize / 1024 * 100) / 100,
                usagePercent: this.config.maxStorageSize ?
                    Math.round((cartSize / this.config.maxStorageSize) * 100) : 0
            };
        } catch (error) {
            logger.warn('💥 Failed to get storage stats:', error);
            return { error: error.message };
        }
    }

    /**
     * Get cart persistence info for debugging
     * @returns {Object}
     */
    getCartInfo() {
        try {
            const cartData = localStorage.getItem(this.cartKey);
            const timestamp = localStorage.getItem(this.cartTimestampKey);
            const paymentInProgress = this.isPaymentInProgress();

            const parsedCartData = cartData ? JSON.parse(cartData) : null;
            return {
                hasCart: !!cartData,
                timestamp: timestamp ? new Date(parseInt(timestamp)) : null,
                age: timestamp ? Date.now() - parseInt(timestamp) : null,
                paymentInProgress,
                itemCount: parsedCartData && parsedCartData.items ? parsedCartData.items.length : 0
            };
        } catch (error) {
            logger.warn('💥 Failed to get cart info:', error);
            return { hasCart: false, error: error.message };
        }
    }
}

const cartPersistenceManager = new CartPersistenceManager();

// Email check cache for performance optimization
class EmailCheckCache {
    constructor() {
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
        this.rateLimitMap = new Map();
        this.rateLimitWindow = 60 * 1000; // 1 minute
        this.maxRequestsPerWindow = 10;
    }

    getCacheKey(email) {
        return email.toLowerCase().trim();
    }

    isRateLimited(email) {
        const key = this.getCacheKey(email);
        const now = Date.now();

        if (!this.rateLimitMap.has(key)) {
            this.rateLimitMap.set(key, []);
        }

        const requests = this.rateLimitMap.get(key);

        // Remove old requests outside the window
        const validRequests = requests.filter(timestamp => now - timestamp < this.rateLimitWindow);
        this.rateLimitMap.set(key, validRequests);

        // Check if we've exceeded the limit
        if (validRequests.length >= this.maxRequestsPerWindow) {
            return true;
        }

        // Add current request
        validRequests.push(now);
        return false;
    }

    get(email) {
        const key = this.getCacheKey(email);
        const cached = this.cache.get(key);

        if (!cached) return null;

        // Check if cache is expired
        if (Date.now() - cached.timestamp > this.cacheTimeout) {
            this.cache.delete(key);
            return null;
        }

        return cached.data;
    }

    set(email, data) {
        const key = this.getCacheKey(email);
        this.cache.set(key, {
            data: data,
            timestamp: Date.now()
        });
    }

    clear() {
        this.cache.clear();
        this.rateLimitMap.clear();
    }
}

const emailCheckCache = new EmailCheckCache();

// Set up request interceptor for secure authentication
ECommerceAPI.interceptors.request.use(config => {
    // Primary: API Key authentication (secure guest access system)
    const apiKey = window.ECOMMERCE_API_KEY || 'ecommerce-key-2025';
    config.headers['X-API-Key'] = apiKey;

    // Optional: Session ID for enhanced user experience
    const sessionId = sessionManager.getOrCreateSession();
    config.headers['X-Session-ID'] = sessionId;

    // Legacy adminuser authentication: check all sources in priority order.
    // updateApiCredentials() sets both HTTP.defaults.headers.post and
    // axios.defaults.headers.common — check both to handle timing edge cases.
    const adminUser = HTTP.defaults.headers.post['adminuser']
        || axios.defaults.headers.post['adminuser']
        || axios.defaults.headers.common['adminuser'];
    if (adminUser) {
        config.headers['adminuser'] = adminUser;
    }

    // API request configured with authentication headers
    return config;
});

// Set up response interceptor for error handling
ECommerceAPI.interceptors.response.use(
    response => {
        // Success response
        return response;
    },
    error => {
        const status = error.response && error.response.status
        // Use warn for 5xx (server errors) — transient DB/server outages are not client bugs.
        // Note: must call logger.warn/error directly (not via extracted reference) to preserve
        // the logger's 'this' context; extracting the method loses 'this' in strict mode.
        if (status && status >= 500) {
            logger.warn('🚨 ECommerce API Error:', {
                status: status,
                statusText: error.response && error.response.statusText,
                data: error.response && error.response.data,
                url: error.config && error.config.url,
                headers: error.config && error.config.headers
            });
        } else {
            logger.error('🚨 ECommerce API Error:', {
                status: status,
                statusText: error.response && error.response.statusText,
                data: error.response && error.response.data,
                url: error.config && error.config.url,
                headers: error.config && error.config.headers
            });
        }

        // Handle specific error cases
        if (error.response && error.response.status === 401) {
            logger.error('❌ Authentication failed - API key required or invalid');
            // Clear session on auth failure
            sessionManager.clearSession();
        } else if (error.response && error.response.status === 429) {
            logger.warn('⚠️ Rate limit exceeded - too many requests');
        }

        return Promise.reject(error);
    }
);

// Enhanced eCommerce API methods
export const ecommerceApiService = {
    
    /**
     * Get products with enhanced categorization (LEGACY - used as fallback)
     * NOTE: This method loads ALL products and is inefficient. Use getProductsByCategory() or getAllCategorizedProducts() instead.
     * @param {Object} params - Query parameters
     * @param {number} params.locationId - Location ID (required)
     * @param {string} params.productCode - Product code filter (optional)
     * @param {number} params.productTypeId - Product type filter (optional, -1 for all)
     * @param {number} params.productLineId - Product line filter (optional, -1 for all)
     * @param {boolean} params.activeOnly - Return only active products (default: true)
     * @param {boolean} params.availableOnly - Return only available products (default: false)
     */
    async getProductsByLocation(params = {}) {
        try {
            const queryParams = new URLSearchParams({
                locationId: params.locationId || window.PAYMENT_LOCATION_ID,
                activeOnly: params.activeOnly !== false ? 'true' : 'false',
                availableOnly: params.availableOnly === true ? 'true' : 'false',
                ...params
            });

            logger.debug('🔄 LEGACY API: Calling productbylocation_ecommerce (fallback method)');

            // Use the new eCommerce API endpoint with secure authentication
            const response = await ECommerceAPI.get(`productbylocation_ecommerce?${queryParams}`);

            logger.debug(`✅ Secure API: ${response.data.productList && response.data.productList.length || 0} products loaded successfully`);

            return response.data;
        } catch (error) {
            logger.warn('💥 Secure API Error: productbylocation_ecommerce', {
                status: error.response && error.response.status,
                message: error.message,
                url: error.config && error.config.url,
                locationId: params.locationId || window.PAYMENT_LOCATION_ID
            });

            // Handle specific error cases
            if (error.response && error.response.status === 401) {
                throw new Error('Authentication failed - API key required or invalid');
            } else if (error.response && error.response.status === 429) {
                throw new Error('Rate limit exceeded - please slow down');
            } else if (error.response && error.response.status === 500) {
                logger.warn('🚨 Backend 500 Error - Database/Location ID issue detected');
                logger.warn('🔍 Check backend logs for database connectivity or location ID configuration');
                throw new Error(`Backend error with location ID ${params.locationId || window.PAYMENT_LOCATION_ID} - check database configuration`);
            }

            throw error;
        }
    },

    /**
     * Diagnostic function to check API configuration and connectivity
     * @param {number} locationId - Location ID to test
     */
    async diagnosticCheck(locationId = null) {
        const testLocationId = locationId || window.PAYMENT_LOCATION_ID;

        logger.debug('🔍 API Diagnostic Check Starting...');
        logger.debug('📍 Testing Location ID:', testLocationId);
        logger.debug('🗄️ Expected Database: SVDB_Westmoreland_dev');
        logger.debug('🔑 API Key:', window.ECOMMERCE_API_KEY ? 'Present' : 'Missing');
        logger.debug('🌐 API Base URL:', window.VUE_CONFIG_API_ECOMMERCE);

        const results = {
            locationId: testLocationId,
            tests: {}
        };

        // Test 1: Basic connectivity
        // NOTE: 'health' endpoint does not exist in backend - commenting out
        // Use productbylocation_ecommerce test instead for connectivity check
        /* try {
            logger.debug('🧪 Test 1: Basic API connectivity...');
            const response = await ECommerceAPI.get('health');
            results.tests.connectivity = { success: true, response: response.data };
            logger.debug('✅ Test 1: API connectivity successful');
        } catch (error) {
            results.tests.connectivity = { success: false, error: error.message };
            logger.debug('❌ Test 1: API connectivity failed:', error.message);
        } */
        results.tests.connectivity = { success: true, note: 'Health endpoint removed - use productbylocation test instead' };

        // Test 2: Location-specific product query
        try {
            logger.debug('🧪 Test 2: Location-specific product query...');
            const response = await ECommerceAPI.get(`productbylocation_ecommerce?locationId=${testLocationId}&activeOnly=true`);
            results.tests.products = { success: true, count: response.data.productList ? response.data.productList.length : 0 };
            logger.debug('✅ Test 2: Product query successful');
        } catch (error) {
            results.tests.products = { success: false, error: error.message, status: error.response ? error.response.status : 'unknown' };
            logger.debug('❌ Test 2: Product query failed:', error.message);
        }

        return results;
    },

    /**
     * Get product types for universal categorization
     * @param {number} locationId - Location ID (optional)
     */
    async getProductTypes(locationId = null) {
        try {
            const queryParams = locationId ? `?locationId=${locationId}` : '';
            const fullUrl = `producttypes_ecommerce${queryParams}`;

            logger.debug(`🔄 Secure API: Calling ${fullUrl} with authentication`);
            logger.debug(`🔄 Secure API: Full URL: ${ECommerceAPI.defaults.baseURL}${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📋 Secure API Response:`, response.data);
            const categories = response.data.productTypeList || response.data.productTypes || [];
            logger.debug(`✅ Secure API: ${categories.length} categories loaded successfully`);

            return response.data;
        } catch (error) {
            logger.warn('💥 Secure API Error: producttypes_ecommerce', {
                status: error.response && error.response.status,
                message: error.message,
                url: error.config && error.config.url,
                data: error.response && error.response.data
            });

            // Handle authentication errors specifically
            if (error.response && error.response.status === 401) {
                throw new Error('Authentication failed - API key required or invalid');
            } else if (error.response && error.response.status === 429) {
                throw new Error('Rate limit exceeded - please slow down');
            }

            throw error;
        }
    },

    /**
     * Get categories from new database-driven API
     * @param {number} locationId - Location ID (default: window.PAYMENT_LOCATION_ID)
     */
    async getCategories(locationId = window.PAYMENT_LOCATION_ID) {
        try {
            const queryParams = `?locationId=${locationId}`;
            const fullUrl = `categories${queryParams}`;

            logger.debug(`🔄 New Categories API: Calling ${fullUrl} with authentication`);
            logger.debug(`🔄 New Categories API: Full URL: ${ECommerceAPI.defaults.baseURL}${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📋 New Categories API Response:`, response.data);
            const categories = response.data.categories || [];
            logger.debug(`✅ New Categories API: ${categories.length} categories loaded successfully`);

            return response.data;
        } catch (error) {
            logger.warn('💥 New Categories API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                url: error.config && error.config.url
            });

            // Enhanced error handling for common issues
            if (error.response && error.response.status === 401) {
                throw new Error('Authentication failed - check API key configuration');
            }
            if (error.response && error.response.status === 429) {
                throw new Error('Rate limit exceeded - too many requests');
            }
            if (error.response && error.response.status === 500) {
                throw new Error('Server error - backend service may be down');
            }

            throw error;
        }
    },

    /**
     * Get categories from enhanced database-driven API (Ecommerce_ProductCategory table)
     * Returns categories with images, icons, slugs, and display settings
     * @param {number} institutionId - Institution ID (default: 1)
     * @param {boolean} visibleOnly - Return only visible categories (default: true)
     */
    async getCategoriesEnhanced(institutionId = 1, visibleOnly = true) {
        try {
            const queryParams = `?institutionId=${institutionId}&visibleOnly=${visibleOnly}`;
            const fullUrl = `categories/db${queryParams}`;

            logger.debug(`🔄 Enhanced Categories API: Calling ${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📋 Enhanced Categories API Response:`, response.data);
            const categories = response.data.categories || [];
            logger.debug(`✅ Enhanced Categories API: ${categories.length} categories loaded with images/icons`);

            return response.data;
        } catch (error) {
            logger.warn('⚠️ Enhanced Categories API not available (will fall back):', {
                status: error.response && error.response.status,
                message: error.message
            });
            throw error;
        }
    },

    /**
     * Get featured categories for homepage display
     * @param {number} institutionId - Institution ID (default: 1)
     * @param {boolean} guestOnly - Return only guest-visible categories (default: false)
     */
    async getFeaturedCategories(institutionId = 1, guestOnly = false) {
        try {
            const queryParams = `?institutionId=${institutionId}&guestOnly=${guestOnly}`;
            const fullUrl = `categories/featured${queryParams}`;

            logger.debug(`🔄 Featured Categories API: Calling ${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`✅ Featured Categories API: ${response.data.categories?.length || 0} featured categories loaded`);

            return response.data;
        } catch (error) {
            logger.warn('💥 Featured Categories API Error:', error.message);
            throw error;
        }
    },

    /**
     * Get category filters for sidebar filtering
     * Returns category types with their categories for multi-select filtering
     * @returns {Object} Response with categoryTypes array and totalProducts count
     */
    async getCategoryFilters() {
        try {
            const fullUrl = 'category_filters';

            logger.debug(`🔄 Category Filters API: Calling ${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📋 Category Filters API Response:`, response.data);
            const categoryTypes = response.data.categoryTypes || [];
            logger.debug(`✅ Category Filters API: ${categoryTypes.length} filter types loaded`);

            return response.data;
        } catch (error) {
            logger.warn('💥 Category Filters API Error:', {
                status: error.response && error.response.status,
                message: error.message
            });
            throw error;
        }
    },

    /**
     * Get net product counts for category filters given current selection
     * Returns how many products would match if each category filter is added
     * @param {string} selectedCategoryIds - Comma-separated list of selected category IDs
     * @returns {Object} Response with categoryCounts array
     */
    async getNetProductCounts(selectedCategoryIds = '', locationId = window.PAYMENT_LOCATION_ID) {
        try {
            const params = new URLSearchParams();
            if (selectedCategoryIds) {
                params.append('selectedCategoryIds', selectedCategoryIds);
            }
            // API always overrides locationId with GetConfigLocationId() (ecommerce sub-salon).
            // Passing PAYMENT_LOCATION_ID here is harmless — the backend ignores it.
            params.append('locationId', locationId);
            const queryString = params.toString();
            const fullUrl = `net_product_counts${queryString ? '?' + queryString : ''}`;

            logger.debug(`🔄 Net Product Counts API: Calling ${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📋 Net Product Counts API Response:`, response.data);
            const categoryCounts = response.data.categoryCounts || [];
            logger.debug(`✅ Net Product Counts API: ${categoryCounts.length} category counts loaded`);

            return response.data;
        } catch (error) {
            logger.warn('💥 Net Product Counts API Error:', {
                status: error.response && error.response.status,
                message: error.message
            });
            throw error;
        }
    },

    /**
     * Get category by URL slug
     * @param {string} slug - Category URL slug (e.g., "uniforms-and-apparel")
     * @param {number} institutionId - Institution ID (default: 1)
     */
    async getCategoryBySlug(slug, institutionId = 1) {
        try {
            const queryParams = `?institutionId=${institutionId}`;
            const fullUrl = `category/slug/${encodeURIComponent(slug)}${queryParams}`;

            logger.debug(`🔄 Category by Slug API: Calling ${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            return response.data;
        } catch (error) {
            if (error.response && error.response.status === 404) {
                return { Successful: false, category: null, message: 'Category not found' };
            }
            logger.warn('💥 Category by Slug API Error:', error.message);
            throw error;
        }
    },

    /**
     * Get all images for a product
     * @param {number} productId - Product ID
     * @param {boolean} activeOnly - Return only active images (default: true)
     */
    async getProductImages(productId, activeOnly = true) {
        try {
            const queryParams = `?activeOnly=${activeOnly}`;
            const fullUrl = `product/${productId}/images${queryParams}`;

            logger.debug(`🔄 Product Images API: Calling ${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            return response.data;
        } catch (error) {
            logger.warn('💥 Product Images API Error:', error.message);
            throw error;
        }
    },

    /**
     * Get primary image for a product
     * @param {number} productId - Product ID
     */
    async getProductPrimaryImage(productId) {
        try {
            const fullUrl = `product/${productId}/image/primary`;

            const response = await ECommerceAPI.get(fullUrl);

            return response.data;
        } catch (error) {
            if (error.response && error.response.status === 404) {
                return { Successful: false, image: null };
            }
            logger.warn('💥 Product Primary Image API Error:', error.message);
            throw error;
        }
    },

    /**
     * Get all images for a category
     * @param {number} categoryId - ProductCategory ID
     * @param {boolean} activeOnly - Return only active images (default: true)
     */
    async getCategoryImages(categoryId, activeOnly = true) {
        try {
            const queryParams = `?activeOnly=${activeOnly}`;
            const fullUrl = `category/${categoryId}/images${queryParams}`;

            logger.debug(`🔄 Category Images API: Calling ${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            return response.data;
        } catch (error) {
            logger.warn('💥 Category Images API Error:', error.message);
            throw error;
        }
    },

    /**
     * Get primary image for a category
     * @param {number} categoryId - ProductCategory ID
     */
    async getCategoryPrimaryImage(categoryId) {
        try {
            const fullUrl = `category/${categoryId}/image/primary`;

            const response = await ECommerceAPI.get(fullUrl);

            return response.data;
        } catch (error) {
            if (error.response && error.response.status === 404) {
                return { Successful: false, image: null };
            }
            logger.warn('💥 Category Primary Image API Error:', error.message);
            throw error;
        }
    },

    /**
     * Get products by category from new database-driven API
     * @param {number} categoryId - Category ID
     * @param {number} locationId - Location ID (default: window.PAYMENT_LOCATION_ID)
     * @param {boolean} availableOnly - Return only available products (default: false)
     */
    async getProductsByCategory(categoryId, locationId = window.PAYMENT_LOCATION_ID, availableOnly = false) {
        try {
            const queryParams = `?locationId=${locationId}&availableOnly=${availableOnly}`;
            const fullUrl = `products/by-category/${categoryId}${queryParams}`;

            logger.debug(`🔄 New Products by Category API: Calling ${fullUrl} with authentication`);
            logger.debug(`🔄 New Products by Category API: Full URL: ${ECommerceAPI.defaults.baseURL}${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📋 New Products by Category API Response:`, response.data);
            const products = response.data.products || [];
            logger.debug(`✅ New Products by Category API: ${products.length} products loaded successfully`);

            return response.data;
        } catch (error) {
            logger.warn('💥 New Products by Category API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                url: error.config && error.config.url
            });

            throw error;
        }
    },

    /**
     * Get featured products from new database-driven API
     * @param {number} locationId - Location ID (default: window.PAYMENT_LOCATION_ID)
     */
    async getFeaturedProducts(locationId = window.PAYMENT_LOCATION_ID) {
        try {
            const queryParams = `?locationId=${locationId}`;
            const fullUrl = `products/featured${queryParams}`;

            logger.debug(`🔄 New Featured Products API: Calling ${fullUrl} with authentication`);
            logger.debug(`🔄 New Featured Products API: Full URL: ${ECommerceAPI.defaults.baseURL}${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📋 New Featured Products API Response:`, response.data);
            const products = response.data.products || [];
            logger.debug(`✅ New Featured Products API: ${products.length} featured products loaded successfully`);

            return response.data;
        } catch (error) {
            logger.warn('💥 New Featured Products API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                url: error.config && error.config.url
            });

            throw error;
        }
    },

    /**
     * Get all products pre-grouped by category from new database-driven API
     * This is the new efficient endpoint that replaces loading all products and doing client-side matching
     * @param {number} locationId - Location ID (default: window.PAYMENT_LOCATION_ID)
     * @param {boolean} availableOnly - Return only available products (default: false)
     */
    async getAllCategorizedProducts(locationId = window.PAYMENT_LOCATION_ID, availableOnly = false) {
        try {
            const queryParams = `?locationId=${locationId}&availableOnly=${availableOnly}`;
            const fullUrl = `products/all-categorized${queryParams}`;

            logger.debug(`🔄 New All Categorized Products API: Calling ${fullUrl} with authentication`);
            logger.debug(`🔄 New All Categorized Products API: Full URL: ${ECommerceAPI.defaults.baseURL}${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📋 New All Categorized Products API Response:`, response.data);
            const categorizedProducts = response.data.categorizedProducts || [];
            const totalProducts = response.data.totalProducts || 0;
            const totalCategories = response.data.totalCategories || 0;

            logger.debug(`✅ New All Categorized Products API: ${totalProducts} products in ${totalCategories} categories loaded successfully`);

            return response.data;
        } catch (error) {
            logger.warn('💥 New All Categorized Products API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                url: error.config && error.config.url
            });

            throw error;
        }
    },

    /**
     * Get products with size/variation grouping from new database-driven API
     * This endpoint groups products by base name and provides size variations
     * @param {number} locationId - Location ID (default: window.PAYMENT_LOCATION_ID)
     * @param {number} categoryId - Optional legacy category filter (ProductTypeID)
     * @param {boolean} availableOnly - Return only available products (default: false)
     * @param {string} categoryIds - Optional comma-separated ProductCategoryIDs for filter system
     */
    async getGroupedProducts(locationId = window.PAYMENT_LOCATION_ID, categoryId = null, availableOnly = false, categoryIds = null) {
        try {
            let queryParams = `?locationId=${locationId}&availableOnly=${availableOnly}`;
            if (categoryId) {
                queryParams += `&categoryId=${categoryId}`;
            }
            // Support new category filter system with multiple ProductCategoryIDs
            if (categoryIds) {
                queryParams += `&categoryIds=${categoryIds}`;
            }
            const fullUrl = `products/grouped${queryParams}`;

            logger.debug(`🔄 New Grouped Products API: Calling ${fullUrl} with authentication`);
            logger.debug(`🔄 New Grouped Products API: Full URL: ${ECommerceAPI.defaults.baseURL}${fullUrl}`);
            if (categoryIds) {
                logger.debug(`🔍 Category filter applied: ${categoryIds}`);
            }

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📋 New Grouped Products API Response:`, response.data);
            const products = response.data.products || [];
            const productCount = response.data.productCount || 0;
            const totalVariations = response.data.totalVariations || 0;

            logger.debug(`✅ New Grouped Products API: ${productCount} grouped products with ${totalVariations} total variations loaded successfully`);

            return response.data;
        } catch (error) {
            logger.warn('💥 New Grouped Products API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                url: error.config && error.config.url
            });

            throw error;
        }
    },

    /**
     * Get product categories (LEGACY - kept for fallback)
     * @param {Object} params - Query parameters
     * @param {number} params.locationId - Location ID
     * @param {boolean} params.libraryOnly - Library only filter
     * @param {boolean} params.excludeLibrary - Exclude library filter
     */
    async getProductCategories(params = {}) {
        try {
            const queryParams = new URLSearchParams(params);

            logger.debug('🔄 LEGACY API: Calling productcategories_ecommerce (fallback method)');

            const response = await ECommerceAPI.get(`productcategories_ecommerce?${queryParams}`);

            logger.debug('✅ Secure API: Product categories loaded successfully');

            return response.data;
        } catch (error) {
            logger.warn('💥 Secure API Error: productcategories_ecommerce', {
                status: error.response && error.response.status,
                message: error.message,
                url: error.config && error.config.url
            });

            // Handle authentication errors specifically
            if (error.response && error.response.status === 401) {
                throw new Error('Authentication failed - API key required or invalid');
            } else if (error.response && error.response.status === 429) {
                throw new Error('Rate limit exceeded - please slow down');
            }

            throw error;
        }
    },

    /**
     * Get categorization information
     */
    async getCategorizationInfo() {
        try {


            const response = await ECommerceAPI.get('categorization_info');



            return response.data;
        } catch (error) {
            logger.warn('💥 Enhanced API Error: categorization_info', error);
            throw error;
        }
    },

    /**
     * Check user's declining balance
     * @param {Object} params - Balance check parameters
     * @param {number} params.personId - Person ID
     * @param {number} params.locationId - Location ID
     */
    async checkUserBalance(params) {
        try {
            logger.debug('🔄 Declining Balance API: Checking user balance', params);

            // institutionId maps to EcommerceBalanceRequest.InstitutionID on the backend.
            // Falls back to window.PAYMENT_INSTITUTION_ID if not provided by the caller.
            // The backend controller also resolves InstitutionID from Ecommerce_InstitutionID
            // config when 0, so 0 is a safe default.
            const institutionId = params.institutionId || window.PAYMENT_INSTITUTION_ID || 0;
            const requestConfig = {};
            if (params.userId && params.userKey) {
                requestConfig.headers = { adminuser: `${params.userId}|${params.userKey}` };
            }
            const response = await ECommerceAPI.post('balance', {
                personId: params.personId,
                institutionId: institutionId,
                locationId: params.locationId
            }, requestConfig);

            logger.debug('✅ Declining Balance API: Balance check successful', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Declining Balance API Error: balance', error);
            logger.warn('💥 Error details:', error.response && error.response.data || error.message);
            throw error;
        }
    },

    /**
     * Process declining balance payment
     * @param {Object} paymentRequest - Payment request data
     * @param {number} paymentRequest.personId - Person ID
     * @param {number} paymentRequest.accountId - Account ID
     * @param {number} paymentRequest.amount - Payment amount
     * @param {string} paymentRequest.orderReference - Order reference
     * @param {string} paymentRequest.description - Payment description
     * @param {Array} paymentRequest.items - Order items
     */
    async createDecliningBalanceTicket({ accountId, locationId, shippingAmount, items, personId }) {
        const response = await ECommerceAPI.post('user/balance/create-ticket', {
            accountId,
            locationId,
            shippingAmount: shippingAmount || 0,
            personId: personId || 0,
            items: items.map(item => ({
                productId: item.productId || item.id,
                name: item.name,
                unitPrice: item.price || item.unitPrice,
                quantity: item.quantity || 1
            }))
        })
        return response.data
    },

    async processDecliningBalancePayment(paymentRequest) {
        try {
            logger.debug('🔄 Declining Balance API: Processing payment', paymentRequest);

            const payload = { orderId: 0, ...paymentRequest };
            const response = await ECommerceAPI.post('balance/pay', payload);

            logger.debug('✅ Declining Balance API: Payment processed successfully', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Declining Balance API Error: balance/pay', error);
            logger.warn('💥 Error details:', error.response && error.response.data || error.message);
            throw error;
        }
    },

    // =========================================================================
    // TICKET OPERATIONS (POS Integration) - Added 2026-01-18
    // =========================================================================

    /**
     * Create a ticket with payment for a single product purchase
     * Used for declining balance purchases that need POS ticket integration
     * @param {Object} request - Ticket creation request
     * @param {number} request.SalonBusinessDayID - The business day ID
     * @param {number} request.ProductID - Product being purchased
     * @param {number} request.UnitPrice - Price per unit
     * @param {number} [request.AccountID] - Optional account ID
     * @param {number} [request.Quantity=1] - Quantity (default: 1)
     * @param {number} [request.AccountTransactionID] - Transaction ID from balance payment
     * @param {boolean} [request.SkipPaymentItem=false] - Set true for split payments
     * @param {Object} [request.deliveryAddress] - Optional per-order delivery address
     * @returns {Object} Response with TicketID, TicketLineItemID, TicketPaymentID, TicketPaymentItemID
     */
    async createTicket(request) {
        try {
            logger.debug('🎫 Ticket API: Creating ticket with payment', request);

            const response = await ECommerceAPI.post('ticket/create', request);

            logger.debug('✅ Ticket API: Ticket created successfully', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Ticket API Error: ticket/create', error);
            logger.warn('💥 Error details:', error.response?.data || error.message);
            return {
                Successful: false,
                TicketID: 0,
                TicketLineItemID: 0,
                TicketPaymentID: 0,
                TicketPaymentItemID: 0,
                Message: error.response?.data?.Message || error.message || 'Failed to create ticket'
            };
        }
    },

    /**
     * Add a payment item to an existing ticket (for split payments)
     * Supports: DecliningBalance, CreditCard, Cash, Check, GiftCard
     * @param {number} ticketId - The ticket ID to add payment to
     * @param {Object} request - Payment item request
     * @param {number} request.TicketID - The ticket ID (must match ticketId param)
     * @param {number} request.PaymentAmount - Amount for this payment
     * @param {string} request.PaymentType - Type: DecliningBalance, CreditCard, Cash, Check, GiftCard
     * @param {string} [request.PaymentSubType] - For CreditCard: Visa, MasterCard, etc.
     * @param {number} [request.AccountTransactionID] - Required for DecliningBalance
     * @param {string} [request.CardNumberLast4] - Last 4 digits for card payments
     * @param {string} [request.AuthorizationCode] - Auth code for card payments
     * @returns {Object} Response with TicketPaymentItemID, PaymentType, PaymentAmount
     */
    async addTicketPaymentItem(ticketId, request) {
        try {
            logger.debug('🎫 Ticket API: Adding payment item to ticket', { ticketId, request });

            // Ensure TicketID in request matches URL param
            const requestWithTicketId = { ...request, TicketID: ticketId };

            const response = await ECommerceAPI.post(`ticket/${ticketId}/payment`, requestWithTicketId);

            logger.debug('✅ Ticket API: Payment item added successfully', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Ticket API Error: ticket/{ticketId}/payment', error);
            logger.warn('💥 Error details:', error.response?.data || error.message);
            return {
                Successful: false,
                TicketPaymentItemID: 0,
                PaymentType: request.PaymentType || '',
                PaymentAmount: 0,
                Message: error.response?.data?.Message || error.message || 'Failed to add payment item'
            };
        }
    },

    /**
     * Create a refund ticket for an existing purchase
     * @param {Object} request - Refund request
     * @param {number} request.OriginalTicketID - The original ticket ID
     * @param {number} request.OriginalTicketLineItemID - The original line item ID
     * @param {number} request.RefundAccountTransactionID - Transaction ID from balance refund
     * @param {number} [request.RefundQuantity] - Quantity to refund (null = full refund)
     * @param {string} [request.Reason] - Reason for refund
     * @returns {Object} Response with RefundTicketID, RefundTicketLineItemID, etc.
     */
    async createRefundTicket(request) {
        try {
            logger.debug('🎫 Ticket API: Creating refund ticket', request);

            const response = await ECommerceAPI.post('ticket/refund', request);

            logger.debug('✅ Ticket API: Refund ticket created successfully', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Ticket API Error: ticket/refund', error);
            logger.warn('💥 Error details:', error.response?.data || error.message);
            return {
                Successful: false,
                RefundTicketID: 0,
                RefundTicketLineItemID: 0,
                RefundTicketPaymentID: 0,
                RefundTicketPaymentItemID: 0,
                Message: error.response?.data?.Message || error.message || 'Failed to create refund ticket'
            };
        }
    },

    /**
     * Update guest information
     * Note: FirstName, LastName, EmailAddress are protected for employee-linked guests
     * Delivery address fields are always updatable
     * @param {Object} request - Guest update request
     * @param {number} request.GuestID - The guest ID to update
     * @param {string} [request.FirstName] - First name (protected for linked guests)
     * @param {string} [request.LastName] - Last name (protected for linked guests)
     * @param {string} [request.EmailAddress] - Email (protected for linked guests)
     * @param {string} [request.PhoneNumber_Home] - Home phone
     * @param {string} [request.PhoneNumber_Auxiliary] - Auxiliary phone
     * @param {Object} [request.deliveryAddress] - Delivery address fields (always updatable)
     * @returns {Object} Response with GuestID, IsLinkedGuest, FieldsUpdated
     */
    async updateGuest(request) {
        try {
            logger.debug('👤 Guest API: Updating guest information', request);

            const response = await ECommerceAPI.post('guest/update', request);

            logger.debug('✅ Guest API: Guest updated successfully', response.data);

            // Log warning if some fields were protected
            if (response.data.IsLinkedGuest && response.data.EmployeeLinked) {
                logger.warn('⚠️ Guest is linked to an employee - some fields may not have been updated');
            }

            return response.data;
        } catch (error) {
            logger.warn('💥 Guest API Error: guest/update', error);
            logger.warn('💥 Error details:', error.response?.data || error.message);
            return {
                Successful: false,
                GuestID: request.GuestID || 0,
                IsLinkedGuest: false,
                EmployeeLinked: false,
                FieldsUpdated: 0,
                Message: error.response?.data?.Message || error.message || 'Failed to update guest'
            };
        }
    },

    /**
     * Get ticket details by ID
     * @param {number} ticketId - The ticket ID
     * @returns {Object} Ticket details including line items and payments
     */
    async getTicketDetails(ticketId) {
        try {
            logger.debug('🎫 Ticket API: Fetching ticket details', { ticketId });

            const response = await ECommerceAPI.get(`ticket/${ticketId}`);

            logger.debug('✅ Ticket API: Ticket details fetched', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Ticket API Error: ticket/{ticketId}', error);
            return {
                Successful: false,
                ticket: null,
                Message: error.response?.data?.Message || error.message || 'Failed to get ticket details'
            };
        }
    },

    // =========================================================================
    // END TICKET OPERATIONS
    // =========================================================================

    /**
     * Initiate WorldPay hosted payment (Enhanced V2 with guest database persistence)
     * @param {Object} paymentRequest - Payment request data
     * @param {number} paymentRequest.locationId - Location ID
     * @param {Array} paymentRequest.initialAccountFundList - Account fund list
     * @param {Object} paymentRequest.guestInfo - Guest information (for guest checkout)
     * @param {Array} paymentRequest.cartItems - Cart items for the order
     * @param {string} recaptchaToken - reCAPTCHA v3 token for bot protection
     */
    async initiatePayment(paymentRequest, recaptchaToken = '') {
        try {
            logger.debug('🔄 WorldPay Payment API V2: Initiating enhanced payment');

            // Configure headers with reCAPTCHA token for bot protection
            const headers = {};
            if (recaptchaToken) {
                headers['X-Recaptcha-Token'] = recaptchaToken;
                logger.debug('🔒 reCAPTCHA token included in request');
            }

            // Use the enhanced V2 endpoint for guest database persistence
            const response = await ECommerceAPI.post('initiate_payment_v2', paymentRequest, { headers });

            logger.debug('✅ WorldPay Payment API V2: Payment initiation successful', response.data);

            // Log enhanced V2 features if available
            if (response.data && response.data.version === 'v2-enhanced-guest') {
                logger.debug('🎉 Enhanced guest creation enabled');
                if (response.data.guestInfo && response.data.guestInfo.processed) {
                    logger.debug('Guest ID:', response.data.guestInfo.processed.guestId);
                    logger.debug('Database persistence:', response.data.guestInfo.processed.enhancedPersistence);
                }
            }

            return response.data;
        } catch (error) {
            logger.warn('💥 WorldPay Payment API V2 Error: initiate_payment_v2', error);
            logger.warn('💥 Error details:', error.response && error.response.data || error.message);
            throw error;
        }
    },

    /**
     * Save order notes to the TicketNote table via Ecommerce_UpdateOrderNotes SP.
     * Pass an empty string to remove an existing note.
     * @param {number} ticketId
     * @param {string} note
     */
    async updateOrderNotes(ticketId, note) {
        try {
            const response = await ECommerceAPI.post('update_order_notes', { ticketId, note: note || '' });
            logger.debug('📝 Order notes saved:', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Failed to save order notes:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Check if email exists in database and get user information (with caching and rate limiting)
     * @param {string} email - Email address to check
     * @returns {Object} Response with EmailExists boolean and user details if found
     */
    async checkEmail(email) {
        try {
            logger.debug('🔍 Email Check API: Checking email existence', { email });

            // Validate email format before sending to API
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                return {
                    success: false,
                    error: 'Invalid email format',
                    EmailExists: false
                };
            }

            // Check rate limiting
            if (emailCheckCache.isRateLimited(email)) {
                logger.warn('⚠️ Email check rate limited for:', email);
                return {
                    success: false,
                    error: 'Too many requests - please slow down',
                    EmailExists: false
                };
            }

            // Check cache first
            const cached = emailCheckCache.get(email);
            if (cached) {
                logger.debug('💾 Email Check Cache Hit:', email);
                return {
                    success: true,
                    ...cached
                };
            }

            logger.debug('🌐 Email Check API Call:', email);
            const response = await ECommerceAPI.post('check_email', { email });

            logger.debug('✅ Email Check API Response:', response.data);

            // Cache the result
            emailCheckCache.set(email, response.data);

            // The backend returns a structured response with EmailExists boolean
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            logger.warn('💥 Email Check API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                data: error.response && error.response.data
            });

            // Handle specific error cases
            if (error.response && error.response.status === 400) {
                return {
                    success: false,
                    error: 'Invalid email format',
                    EmailExists: false
                };
            } else if (error.response && error.response.status === 401) {
                return {
                    success: false,
                    error: 'Authentication failed',
                    EmailExists: false
                };
            } else if (error.response && error.response.status === 429) {
                return {
                    success: false,
                    error: 'Rate limit exceeded - please slow down',
                    EmailExists: false
                };
            }

            return {
                success: false,
                error: error.message || 'Failed to check email',
                EmailExists: false
            };
        }
    },

    /**
     * Request email receipt for transaction
     * @param {Object} receiptRequest - Receipt request data
     * @param {string} receiptRequest.email - Email address
     * @param {string} receiptRequest.transactionId - Transaction ID
     * @param {number} receiptRequest.locationId - Location ID
     */
    async requestEmailReceipt(receiptRequest) {
        try {
            logger.debug('📧 Email Receipt API: Requesting receipt', receiptRequest);

            const response = await ECommerceAPI.post('transaction_lookup', receiptRequest);

            logger.debug('✅ Email Receipt API: Request successful', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Email Receipt API Error: transaction_lookup', error);
            logger.warn('💥 Error details:', error.response && error.response.data || error.message);
            throw error;
        }
    },

    /**
     * Get receipt data using secure token
     * @param {Object} tokenRequest - Token request data
     * @param {string} tokenRequest.receiptToken - Secure receipt token
     * @param {boolean} tokenRequest.consumeToken - Whether to consume the token (default: false)
     */
    async getReceiptByToken(tokenRequest) {
        try {
            logger.debug('🔐 Secure Receipt API: Getting receipt by token', {
                token: tokenRequest.receiptToken ? tokenRequest.receiptToken.substring(0, 10) + '...' : 'undefined',
                consumeToken: tokenRequest.consumeToken
            });

            const response = await ECommerceAPI.post('receipt_token', tokenRequest, {
                headers: {
                    'X-API-Key': window.ECOMMERCE_API_KEY || 'ecommerce-key-2025'
                }
            });

            logger.debug('✅ Secure Receipt API: Token validation successful', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Secure Receipt API Error: receipt_token', error);
            logger.warn('💥 Error details:', error.response && error.response.data || error.message);
            throw error;
        }
    },

    /**
     * Email receipt using secure token
     * @param {Object} emailRequest - Email request data
     * @param {string} emailRequest.receiptToken - Secure receipt token
     * @param {string} emailRequest.emailAddress - Email address to send to
     */
    async emailReceiptByToken(emailRequest) {
        try {
            logger.debug('📧 Secure Email Receipt API: Sending receipt by token', {
                token: emailRequest.receiptToken ? emailRequest.receiptToken.substring(0, 10) + '...' : 'undefined',
                email: emailRequest.emailAddress
            });

            const response = await ECommerceAPI.post('email_receipt', emailRequest, {
                headers: {
                    'X-API-Key': window.ECOMMERCE_API_KEY || 'ecommerce-key-2025'
                }
            });

            logger.debug('✅ Secure Email Receipt API: Email sent successfully', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Secure Email Receipt API Error: email_receipt', error);
            logger.warn('💥 Error details:', error.response && error.response.data || error.message);
            throw error;
        }
    },

    /**
     * Get available pickup locations for eCommerce orders
     * Uses the new pickup-locations endpoint from the backend
     */
    async getPickupLocations() {
        try {
            const fullUrl = 'pickup-locations';

            logger.debug(`🔄 Pickup Locations API: Calling ${fullUrl} with authentication`);
            logger.debug(`🔄 Pickup Locations API: Full URL: ${ECommerceAPI.defaults.baseURL}${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug(`📍 Pickup Locations API Response:`, response.data);
            const pickupLocations = response.data.pickupLocations || [];
            logger.debug(`✅ Pickup Locations API: ${pickupLocations.length} pickup locations loaded successfully`);

            return {
                Successful: true,
                pickupLocations: pickupLocations
            };
        } catch (error) {
            logger.warn('💥 Pickup Locations API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                url: error.config && error.config.url
            });

            // Return a structured error response
            return {
                Successful: false,
                Message: error.message || 'Failed to load pickup locations',
                pickupLocations: []
            };
        }
    },

    /**
     * Send customer receipt notification with pickup location details
     * @param {Object} receiptRequest - Customer receipt notification data
     * @param {string} receiptRequest.customerEmail - Customer email address
     * @param {string} receiptRequest.transactionId - Transaction ID
     * @param {number} receiptRequest.pickupLocationId - Pickup location ID
     * @param {string} receiptRequest.pickupLocationName - Pickup location name
     * @param {string} receiptRequest.pickupLocationAddress - Pickup location address
     * @param {number} receiptRequest.orderTotal - Order total amount
     * @param {string} receiptRequest.orderItems - Order items description
     */
    async sendCustomerReceiptNotification(receiptRequest) {
        try {
            logger.debug('📧 Customer Receipt Notification API: Sending notification', receiptRequest);

            const response = await ECommerceAPI.post('send-customer-receipt', receiptRequest);

            logger.debug('✅ Customer Receipt Notification API: Notification sent successfully', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Customer Receipt Notification API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                data: error.response && error.response.data
            });

            // Return a structured error response
            return {
                Successful: false,
                Message: error.message || 'Failed to send customer receipt notification'
            };
        }
    },

    /**
     * Send pickup notification to institution
     * @param {Object} notificationRequest - Pickup notification data
     * @param {string} notificationRequest.customerName - Customer name
     * @param {string} notificationRequest.customerEmail - Customer email address
     * @param {string} notificationRequest.customerPhone - Customer phone number
     * @param {string} notificationRequest.transactionId - Transaction ID
     * @param {number} notificationRequest.pickupLocationId - Pickup location ID
     * @param {string} notificationRequest.pickupLocationName - Pickup location name
     * @param {number} notificationRequest.orderTotal - Order total amount
     * @param {string} notificationRequest.orderItems - Order items description
     */
    async sendPickupNotification(notificationRequest) {
        try {
            logger.debug('📧 Pickup Notification API: Sending notification', notificationRequest);

            const response = await ECommerceAPI.post('send-pickup-notification', notificationRequest);

            logger.debug('✅ Pickup Notification API: Notification sent successfully', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Pickup Notification API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                data: error.response && error.response.data
            });

            // Return a structured error response
            return {
                Successful: false,
                Message: error.message || 'Failed to send pickup notification'
            };
        }
    },

    /**
     * Get shipping configuration
     * Returns whether shipping is enabled and its configuration
     */
    async getShippingConfig() {
        try {
            const fullUrl = 'shipping/config';

            logger.debug(`🔄 Shipping Config API: Calling ${fullUrl}`);

            const response = await ECommerceAPI.get(fullUrl);

            logger.debug('✅ Shipping Config API Response:', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Shipping Config API Error:', {
                status: error.response && error.response.status,
                message: error.message
            });

            // Return default disabled state on error
            return {
                Successful: false,
                isEnabled: false,
                Message: error.message || 'Failed to get shipping configuration'
            };
        }
    },

    /**
     * Get shipping quotes for cart items
     * @param {Object} quoteRequest - Shipping quote request
     * @param {number} quoteRequest.salonId - Salon/Location ID
     * @param {Object} quoteRequest.toAddress - Destination address
     * @param {string} quoteRequest.toAddress.name - Recipient name
     * @param {string} quoteRequest.toAddress.street1 - Street address line 1
     * @param {string} quoteRequest.toAddress.street2 - Street address line 2 (optional)
     * @param {string} quoteRequest.toAddress.city - City
     * @param {string} quoteRequest.toAddress.state - State code (e.g., "PA")
     * @param {string} quoteRequest.toAddress.postalCode - ZIP code
     * @param {string} quoteRequest.toAddress.country - Country code (default: "US")
     * @param {Array} quoteRequest.items - Cart items with product details
     * @param {number} quoteRequest.orderSubtotal - Order subtotal for free shipping calculation
     */
    async getShippingQuotes(quoteRequest) {
        try {
            logger.debug('🚚 Shipping Quotes API: Requesting quotes', quoteRequest);

            const response = await ECommerceAPI.post('shipping/quotes', quoteRequest);

            logger.debug('✅ Shipping Quotes API Response:', response.data);
            return response.data;
        } catch (error) {
            logger.warn('💥 Shipping Quotes API Error:', {
                status: error.response && error.response.status,
                message: error.message,
                data: error.response && error.response.data
            });

            // Return a structured error response
            return {
                Successful: false,
                rates: [],
                Message: error.message || 'Failed to get shipping quotes'
            };
        }
    },

    // ========================================
    // FULFILLMENT ADMIN API METHODS
    // ========================================

    /**
     * Get list of fulfillments for admin management
     * @param {Object} filters - Optional filters
     * @param {string} filters.status - Filter by status (PENDING, PROCESSING, SHIPPED, etc.)
     * @param {string} filters.fulfillmentType - Filter by type (SHIPPING, PICKUP)
     * @returns {Object} Response with fulfillments array
     */
    async getFulfillments(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.fulfillmentType) params.append('fulfillmentType', filters.fulfillmentType);

            const queryString = params.toString();
            const url = `fulfillments${queryString ? '?' + queryString : ''}`;

            logger.debug('📦 Fulfillments API: Fetching fulfillments', { url, filters });
            const response = await ECommerceAPI.get(url);
            logger.debug('✅ Fulfillments API Response:', response.data);

            return response.data;
        } catch (error) {
            logger.warn('💥 Fulfillments API Error:', {
                status: error.response?.status,
                message: error.message,
                data: error.response?.data
            });
            return {
                Successful: false,
                fulfillments: [],
                Message: error.message || 'Failed to get fulfillments'
            };
        }
    },

    /**
     * Get detailed fulfillment info for a specific ticket
     * @param {number} ticketId - The ticket ID
     * @returns {Object} Response with fulfillment details
     */
    async getFulfillmentByTicket(ticketId) {
        try {
            logger.debug('📦 Fulfillment Detail API: Fetching ticket', ticketId);
            const response = await ECommerceAPI.get(`fulfillment/${ticketId}`);
            logger.debug('✅ Fulfillment Detail API Response:', response.data);

            return response.data;
        } catch (error) {
            logger.warn('💥 Fulfillment Detail API Error:', {
                ticketId,
                status: error.response?.status,
                message: error.message
            });
            return {
                Successful: false,
                fulfillment: null,
                Message: error.message || 'Failed to get fulfillment details'
            };
        }
    },

    /**
     * Update fulfillment status with tracking info
     * @param {number} fulfillmentId - The fulfillment ID
     * @param {Object} updateData - Status update data
     * @param {string} updateData.Status - New status (PENDING, PROCESSING, SHIPPED, DELIVERED, etc.)
     * @param {string} updateData.TrackingNumber - Tracking number (for shipped items)
     * @param {string} updateData.TrackingUrl - Tracking URL
     * @param {string} updateData.CarrierName - Carrier name (USPS, UPS, FedEx, etc.)
     * @param {string} updateData.Notes - Internal notes
     * @returns {Object} Response with update result
     */
    async updateFulfillmentStatus(fulfillmentId, updateData) {
        try {
            logger.debug('📦 Fulfillment Update API: Updating status', { fulfillmentId, updateData });
            const response = await ECommerceAPI.put(`fulfillment/${fulfillmentId}/status`, updateData);
            logger.debug('✅ Fulfillment Update API Response:', response.data);

            return response.data;
        } catch (error) {
            logger.warn('💥 Fulfillment Update API Error:', {
                fulfillmentId,
                status: error.response?.status,
                message: error.message
            });
            return {
                Successful: false,
                Message: error.message || 'Failed to update fulfillment status'
            };
        }
    },

    // ── Genius Test Harness ────────────────────────────────────────────

    async getGeniusTestConfig() {
        const response = await ECommerceAPI.get('genius-test/config');
        return response.data;
    },

    async getGeniusTestConnectivity() {
        const response = await ECommerceAPI.get('genius-test/connectivity');
        return response.data;
    },

    async postGeniusTestDryRun(amount = '1.00', action = 'sale') {
        const response = await ECommerceAPI.post('genius-test/dry-run', { amount, action });
        return response.data;
    },

    async getGeniusTestFull() {
        const response = await ECommerceAPI.get('genius-test/full');
        return response.data;
    },

    async postGeniusTestLiveSale(amount = '1.00') {
        const response = await ECommerceAPI.post('genius-test/live-sale', { amount });
        return response.data;
    },

    async getGeniusTestVerifyPayment(transactionRef) {
        const response = await ECommerceAPI.get(`genius-test/verify-payment?transactionRef=${encodeURIComponent(transactionRef)}`);
        return response.data;
    },

    // ── Declining Balance / Account Test ──────────────────────────────────────

    async postAccountTest({ accountId = 0, personId = 0, productId = 0, amount = 0.01, cleanup = true } = {}) {
        const response = await ECommerceAPI.post('account_test', { accountId, personId, productId, amount, cleanup });
        return response.data;
    },

    async getDecliningBalanceConfig() {
        const response = await ECommerceAPI.get('declining-balance-test/config');
        return response.data;
    },

    async postDecliningBalanceCheckBalance(personId, institutionId = 0) {
        const response = await ECommerceAPI.post('declining-balance-test/check-balance', { personId, institutionId });
        return response.data;
    },

    async postDecliningBalancePersonSearch(firstName, lastName) {
        const response = await ECommerceAPI.post('declining-balance-test/person-search', { firstName, lastName });
        return response.data;
    },

    async getDecliningBalanceProducts() {
        const response = await ECommerceAPI.get('declining-balance-test/products');
        return response.data;
    }
};

// Utility functions for WorldPay payments
export const paymentUtils = {
    /**
     * Format cart items for payment
     * @param {Array} cartItems - Cart items
     * @returns {Array} Formatted cart items
     */
    formatCartItemsForPayment(cartItems) {
        return cartItems.map(item => ({
            productId: item.id,
            productName: item.name,
            quantity: item.quantity,
            unitPrice: item.price,
            totalPrice: item.price * item.quantity,
            glCode: item.glCode || 'ECOMMERCE'
        }));
    },

    /**
     * Create payment request for WorldPay hosted payment (Enhanced V2 format)
     * @param {Object} params - Payment parameters
     * @param {Array} params.cartItems - Cart items
     * @param {number} params.cartTotal - Cart total amount (should include shipping)
     * @param {Object} params.guestInfo - Guest information (optional)
     * @param {number} params.locationId - Location ID
     * @param {Object} params.pickupLocation - Pickup location information (optional)
     * @param {Object} params.shipping - Shipping information (optional)
     * @returns {Object} Formatted payment request optimized for V2 endpoint
     */
    createPaymentRequest(params) {
        const { cartItems, cartTotal, guestInfo, locationId, pickupLocation, shipping, personId } = params;

        // Use configured location ID or fallback to enhanced default
        const paymentLocationId = locationId || window.PAYMENT_ECOMMERCE_LOCATION_ID || window.PAYMENT_LOCATION_ID;

        // Generate unique transaction reference
        const transactionReference = 'TXN_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8).toUpperCase();

        // Create V2-optimized payload structure
        const v2PaymentRequest = {
            locationId: paymentLocationId,
            transactionReference: transactionReference
        };

        // Enhanced V2 guest information structure
        if (guestInfo) {
            v2PaymentRequest.guestInfo = {
                firstName: guestInfo.firstName,
                lastName: guestInfo.lastName,
                email: guestInfo.email,
                // Optional additional fields
                phone: guestInfo.phone || null,
                address: guestInfo.address || null
            };
        }

        // Enhanced V2 cart items structure
        if (cartItems && cartItems.length > 0) {
            v2PaymentRequest.cartItems = cartItems.map(item => ({
                productId: item.id || item.productId,
                productName: item.name || item.productName,
                quantity: item.quantity,
                unitPrice: item.price || item.unitPrice,
                totalPrice: (item.price || item.unitPrice) * item.quantity,
                glCode: item.glCode || 'ECOMMERCE'
            }));
        }

        // Add total amount (V2 endpoint expects this structure)
        v2PaymentRequest.totalAmount = cartTotal;

        // For logged-in users: include personId so the backend can resolve the correct
        // POS GuestID (Guest.GuestID where Guest.EmployeeID = Person.PersonID).
        // This is distinct from the web "guest" concept — every person has a Guest record.
        if (personId) {
            v2PaymentRequest.personId = personId;
        }

        // Add pickup location information if provided
        if (pickupLocation) {
            v2PaymentRequest.pickupLocation = {
                id: pickupLocation.id,
                name: pickupLocation.name,
                addressLine1: pickupLocation.addressLine1,
                addressLine2: pickupLocation.addressLine2,
                city: pickupLocation.city,
                stateId: pickupLocation.stateId,
                postalCode: pickupLocation.postalCode,
                phoneNumber: pickupLocation.phoneNumber,
                emailAddress: pickupLocation.emailAddress
            };
        }

        // SHIPPING FIX: Add shipping information if provided
        if (shipping && shipping.amount > 0) {
            v2PaymentRequest.shipping = {
                rateId: shipping.rateId,
                carrier: shipping.carrier,
                service: shipping.service,
                amount: shipping.amount,
                address: shipping.address
            };
            logger.debug('🚚 Including shipping in payment request:', v2PaymentRequest.shipping);
        }

        // Add return URLs for enhanced redirect handling
        if (window.INCLUDE_EXTENDED_PAYMENT_DATA !== false) {
            v2PaymentRequest.returnUrls = {
                // Main return URL - where WorldPay should redirect users
                returnUrl: window.PAYMENT_RESPONSE_URL || (window.location.origin + '/payment-response'),
                successUrl: window.PAYMENT_SUCCESS_URL || (window.location.origin + '/payment-success'),
                cancelUrl: window.PAYMENT_CANCEL_URL || (window.location.origin + '/payment-cancelled'),
                errorUrl: window.PAYMENT_ERROR_URL || (window.location.origin + '/payment-error')
            };
            v2PaymentRequest.callbackUrl = window.PAYMENT_CALLBACK_URL || (window.VUE_CONFIG_API_ECOMMERCE.replace('/api/ecommerce/', '/api/payresponse'));
        }

        logger.debug('🎯 V2 Payment Request Structure:', v2PaymentRequest);
        return v2PaymentRequest;
    }
};

// Utility functions for declining balance payments
export const decliningBalanceUtils = {
    /**
     * Get authenticated user's person ID
     * @param {Object} user - Logged in user object from store
     * @returns {number|null} Person ID or null if not available
     */
    getAuthenticatedPersonId(user) {
        return user && user.user_personId ? user.user_personId : null;
    },

    /**
     * Get authenticated user's location ID
     * @param {Object} user - Logged in user object from store
     * @returns {number|null} Location ID or null if not available
     */
    getAuthenticatedLocationId(user) {
        return user && user.LocationId ? user.LocationId : null;
    },

    /**
     * Generate unique order reference
     * @returns {string} Unique order reference
     */
    generateOrderReference() {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 1000);
        return `ORDER-${timestamp}-${random}`;
    },

    /**
     * Check if user is authenticated and has required data for declining balance
     * @param {Object} user - Logged in user object from store
     * @returns {boolean} True if user can use declining balance
     */
    canUseDeclineBalance(user) {
        return !!(user && user.user_personId && user.LocationId);
    },

    /**
     * Format cart items for payment API
     * @param {Array} cartItems - Cart items from store
     * @returns {Array} Formatted items for API
     */
    formatCartItemsForPayment(cartItems) {
        return cartItems.map(item => ({
            productId: item.id,
            productName: item.name,
            quantity: item.quantity,
            unitPrice: item.price,
            totalPrice: item.price * item.quantity,
            glCode: item.glCode || 'ECOMMERCE'
        }));
    }
};

// Export session manager for external use
export const ecommerceSessionManager = sessionManager;

// Export utility functions for authentication testing
export const ecommerceAuthUtils = {
    /**
     * Test different API keys to find the correct one
     */
    async testApiKeys() {
        const testKeys = [
            'ecommerce-guest-key-2025',
            'ecommerce-key-2025',
            'guest-access-key-2025',
            'ecommerce-api-key-2025',
            'secure-guest-access-2025',
            'ecommerce-readonly-2025',
            'guest-ecommerce-2025'
        ];

        logger.debug('🧪 Testing different API keys...');

        for (const testKey of testKeys) {
            try {
                logger.debug(`🔑 Testing API key: ${testKey}`);

                // Temporarily override the API key
                const originalKey = window.ECOMMERCE_API_KEY;
                window.ECOMMERCE_API_KEY = testKey;

                // Test the API call
                const response = await fetch(`${window.VUE_CONFIG_API_ECOMMERCE}producttypes_ecommerce?locationId=${window.PAYMENT_LOCATION_ID}`, {
                    headers: {
                        'X-API-Key': testKey,
                        'Content-Type': 'application/json'
                    }
                });

                // Restore original key
                window.ECOMMERCE_API_KEY = originalKey;

                if (response.ok) {
                    logger.debug(`✅ SUCCESS! API key works: ${testKey}`);
                    return { success: true, workingKey: testKey, status: response.status };
                } else {
                    logger.debug(`❌ Failed with status ${response.status}: ${testKey}`);
                }
            } catch (error) {
                logger.debug(`💥 Error testing ${testKey}:`, error.message);
            }
        }

        logger.debug('❌ No working API key found');
        return { success: false, message: 'No working API key found' };
    },
    /**
     * Test API integration with current authentication
     */
    async testApiIntegration() {
        try {
            logger.debug('🧪 Testing API integration...');

            // Test 1: Valid request
            const products = await ecommerceApiService.getProductTypes();
            logger.debug('✅ API integration working:', products);

            // Test 2: Session persistence
            const sessionId1 = sessionManager.getOrCreateSession();
            const sessionId2 = sessionManager.getOrCreateSession();
            logger.debug('✅ Session persistence:', sessionId1 === sessionId2);

            return { success: true, message: 'API integration test passed' };
        } catch (error) {
            logger.error('❌ API integration failed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Get current session information
     */
    getSessionInfo() {
        const sessionId = localStorage.getItem('ecommerce-session');
        const created = localStorage.getItem('ecommerce-session-created');

        if (!sessionId || !created) {
            return { hasSession: false };
        }

        const sessionAge = Date.now() - parseInt(created);
        const maxAge = 60 * 60 * 1000; // 60 minutes
        const isExpired = sessionAge > maxAge;

        return {
            hasSession: true,
            sessionId,
            created: new Date(parseInt(created)),
            age: sessionAge,
            isExpired,
            remainingTime: isExpired ? 0 : maxAge - sessionAge
        };
    },

    /**
     * Clear session manually
     */
    clearSession() {
        sessionManager.clearSession();
        logger.debug('🗑️ Session cleared manually');
    },

    /**
     * Cart persistence methods
     */
    saveCartToStorage(cartState) {
        cartPersistenceManager.saveCart(cartState);
    },

    restoreCartFromStorage() {
        return cartPersistenceManager.restoreCart();
    },

    clearCartFromStorage() {
        cartPersistenceManager.clearCart();
    },

    markPaymentInProgress() {
        cartPersistenceManager.markPaymentInProgress();
    },

    clearPaymentInProgress() {
        cartPersistenceManager.clearPaymentInProgress();
    },

    isPaymentInProgress() {
        return cartPersistenceManager.isPaymentInProgress();
    },

    getCartPersistenceInfo() {
        return cartPersistenceManager.getCartInfo();
    },

    /**
     * Configuration and utility methods
     */
    getCartPersistenceConfig() {
        return CartPersistenceConfig;
    },

    updateCartPersistenceConfig(newConfig) {
        Object.assign(CartPersistenceConfig, newConfig);
        logger.debug('🔧 Cart persistence configuration updated:', newConfig);
    },

    getStorageStats() {
        return cartPersistenceManager.getStorageStats();
    },

    cleanupExpiredData() {
        cartPersistenceManager.cleanupExpiredData();
    },

    /**
     * Advanced cart recovery service with multiple fallback mechanisms
     * @param {string} transactionReference - Optional transaction reference for backend recovery
     * @returns {Object|null} - Recovered cart data or null
     */
    async recoverCart(transactionReference = null) {
        logger.debug('🔄 Starting cart recovery process...');

        // Strategy 1: Try localStorage first (fastest)
        const localCart = this.restoreCartFromStorage();
        if (localCart && localCart.items && localCart.items.length > 0) {
            logger.debug('✅ Cart recovered from localStorage');
            return localCart;
        }

        // Strategy 2: Try backend session recovery if transaction reference is available
        if (transactionReference) {
            try {
                logger.debug('🔄 Attempting backend cart recovery...');
                const backendCart = await this.recoverCartFromBackend(transactionReference);
                if (backendCart) {
                    logger.debug('✅ Cart recovered from backend session');
                    // Save recovered cart to localStorage for future use
                    this.saveCartToStorage(backendCart);
                    return backendCart;
                }
            } catch (error) {
                logger.warn('⚠️ Backend cart recovery failed:', error.message);
            }
        }

        // Strategy 3: Check if payment was in progress and preserve empty cart state
        if (this.isPaymentInProgress()) {
            logger.debug('💳 Payment in progress detected, preserving cart state');
            return {
                items: [],
                total: 0,
                guestInfo: {},
                taxAmount: 0,
                totalWithTax: 0,
                paymentInProgress: true
            };
        }

        logger.debug('❌ No cart data could be recovered');
        return null;
    },

    /**
     * Attempt to recover cart from backend session storage
     * @param {string} transactionReference - Transaction reference for cart lookup
     * @returns {Object|null} - Cart data from backend or null
     */
    async recoverCartFromBackend(transactionReference) {
        try {
            // This would call a backend endpoint to recover cart items
            // Based on the transaction reference or session ID
            const response = await ECommerceAPI.post('recover_cart', {
                transactionReference,
                sessionId: sessionManager.getOrCreateSession()
            });

            if (response.Successful && response.cartData) {
                return {
                    items: response.cartData.items || [],
                    total: response.cartData.total || 0,
                    guestInfo: response.cartData.guestInfo || {},
                    taxAmount: response.cartData.taxAmount || 0,
                    totalWithTax: response.cartData.totalWithTax || 0
                };
            }

            return null;
        } catch (error) {
            logger.warn('💥 Backend cart recovery failed:', error);
            return null;
        }
    },

    /**
     * Smart cart recovery for payment return scenarios
     * Checks URL parameters for transaction references and attempts recovery
     * @returns {Object|null} - Recovered cart data or null
     */
    async recoverCartFromPaymentReturn() {
        try {
            // Check URL parameters for transaction reference
            const urlParams = new URLSearchParams(window.location.search);
            const transactionRef = urlParams.get('transactionReference') ||
                                 urlParams.get('orderReference') ||
                                 urlParams.get('ticketId');

            if (transactionRef) {
                logger.debug('🔄 Payment return detected, attempting cart recovery with reference:', transactionRef);
                return await this.recoverCart(transactionRef);
            }

            // Fallback to standard recovery
            return await this.recoverCart();
        } catch (error) {
            logger.warn('💥 Payment return cart recovery failed:', error);
            try {
                return await this.recoverCart();
            } catch {
                return null;
            }
        }
    },

    async suspendCart(payload) {
        try {
            const response = await ECommerceAPI.post('suspend-cart', payload)
            return response.data
        } catch (error) {
            logger.warn('💥 suspendCart error:', error.response?.data || error.message)
            throw error
        }
    },

    async getSuspendedTickets() {
        try {
            const response = await ECommerceAPI.get('my-suspended-tickets')
            return response.data
        } catch (error) {
            logger.warn('💥 getSuspendedTickets error:', error.response?.data || error.message)
            throw error
        }
    },

    async restoreSuspendedTicket(ticketId) {
        try {
            const response = await ECommerceAPI.post(`restore-suspended-ticket/${ticketId}`, {})
            return response.data
        } catch (error) {
            logger.warn('💥 restoreSuspendedTicket error:', error.response?.data || error.message)
            throw error
        }
    }
};

// Bridge methods from ecommerceAuthUtils into ecommerceApiService so the store can
// call them via the default import (e.g. `import apiService from '...'`).
ecommerceApiService.recoverCartFromPaymentReturn = ecommerceAuthUtils.recoverCartFromPaymentReturn.bind(ecommerceAuthUtils);
ecommerceApiService.suspendCart = ecommerceAuthUtils.suspendCart.bind(ecommerceAuthUtils);
ecommerceApiService.getSuspendedTickets = ecommerceAuthUtils.getSuspendedTickets.bind(ecommerceAuthUtils);
ecommerceApiService.restoreSuspendedTicket = ecommerceAuthUtils.restoreSuspendedTicket.bind(ecommerceAuthUtils);

// Export both the service and the cart persistence components
export { cartPersistenceManager, CartPersistenceConfig };
export default ecommerceApiService;
